import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import API_BASE_URL from '../config';
import { FaCalendarAlt } from 'react-icons/fa';
import styles from '../css-isolated/su_jo_search_consultation.module.css';
import ChangePasswordModal from './change_pass';
import authFetch from '../utils/authFetch';
import { lettersOnly } from '../utils/inputValidation';

function PdlConsultationSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);
  const [isDarkMode, setIsDarkMode] = React.useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || savedTheme === null;
  });
  const [showThemeModal, setShowThemeModal] = React.useState(false);
  const [pendingTheme, setPendingTheme] = React.useState(null);
  const [showLogoutModal, setShowLogoutModal] = React.useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState('');
  const [showErrorModal, setShowErrorModal] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  
  // Date Modal State
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [modalMonth, setModalMonth] = useState('');
  const [modalDay, setModalDay] = useState('');
  const [modalYear, setModalYear] = useState('');
  const [modalTextDate, setModalTextDate] = useState('');
  const [modalError, setModalError] = useState('');
  
  // Date Range State
  const [dateRangeMode, setDateRangeMode] = useState('single'); // 'single' or 'range'
  const [isStartDateModal, setIsStartDateModal] = useState(true);
  const [startModalMonth, setStartModalMonth] = useState('');
  const [startModalDay, setStartModalDay] = useState('');
  const [startModalYear, setStartModalYear] = useState('');
  const [startModalTextDate, setStartModalTextDate] = useState('');
  const [endModalMonth, setEndModalMonth] = useState('');
  const [endModalDay, setEndModalDay] = useState('');
  const [endModalYear, setEndModalYear] = useState('');
  const [endModalTextDate, setEndModalTextDate] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  const [searchData, setSearchData] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    akaNickname: '',
    consultationDate: '',
    startDate: '',
    endDate: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Helper function for date validation
  const getDaysInMonth = (month, year) => {
    if (!month || !year) return 31;
    const monthNum = parseInt(month);
    if (monthNum === 2) {
      const yearNum = parseInt(year);
      const isLeapYear = (yearNum % 4 === 0 && yearNum % 100 !== 0) || (yearNum % 400 === 0);
      return isLeapYear ? 29 : 28;
    }
    if ([4, 6, 9, 11].includes(monthNum)) return 30;
    return 31;
  };

  // Format date to MM/DD/YYYY for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Update time every second
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load user data from localStorage
  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUserData(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Auto-load all consultations when navigated from the consultation KPI card
  React.useEffect(() => {
    if (!location.state?.autoLoad) return;
    const fetchAllConsultations = async () => {
      setIsLoading(true);
      try {
        const response = await authFetch(`${API_BASE_URL}/api/search-consultations`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data.success) {
          const transformedResults = data.consultations.map(consultation => ({
            id: consultation.id,
            patientName: `${consultation.hr_info?.first_name || ''} ${consultation.hr_info?.middle_name || ''} ${consultation.hr_info?.last_name || ''}`.trim(),
            lastName: consultation.hr_info?.last_name || 'N/A',
            firstName: consultation.hr_info?.first_name || 'N/A',
            middleName: consultation.hr_info?.middle_name || 'N/A',
            akaNickname: consultation.hr_info?.alias_nickname || 'N/A',
            date_of_birth: consultation.hr_info?.date_of_birth,
            cell: consultation.cell,
            consultationDate: consultation.date,
            consultationType: consultation.cell_dorm || 'General',
            doctor: consultation.nod_md || 'N/A',
            status: consultation.is_archive ? 'Archived' : 'Active',
            chief_complaint: consultation.chief_complaint || 'N/A',
            diagnosis: consultation.diagnosis || 'N/A',
            diagnostics: consultation.diagnostics || 'N/A',
            referral: consultation.referral || 'N/A',
            accountCreator: consultation.accountCreator,
            accountUpdater: consultation.accountUpdater,
            nod_md_create: consultation.nod_md_create,
            nod_md_update: consultation.nod_md_update
          }));
          setSearchResults(transformedResults);
        }
      } catch (error) {
        console.error('Error fetching all consultations:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAllConsultations();
  }, [location.state]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const calculateAge = React.useCallback((dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  }, []);

  // Helper function to capitalize names
  const capitalizeName = React.useCallback((name) => {
    if (!name) return 'N/A';
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, []);

  // Sorting logic
  const sortedResults = React.useMemo(() => {
    if (!sortColumn) return searchResults;

    const sorted = [...searchResults].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'lastName':
          aValue = (a.lastName || '').toLowerCase();
          bValue = (b.lastName || '').toLowerCase();
          break;
        case 'firstName':
          aValue = (a.firstName || '').toLowerCase();
          bValue = (b.firstName || '').toLowerCase();
          break;
        case 'middleName':
          aValue = (a.middleName || '').toLowerCase();
          bValue = (b.middleName || '').toLowerCase();
          break;
        case 'aka':
          aValue = (a.akaNickname || '').toLowerCase();
          bValue = (b.akaNickname || '').toLowerCase();
          break;
        case 'age':
          const ageA = calculateAge(a.date_of_birth);
          const ageB = calculateAge(b.date_of_birth);
          aValue = ageA === 'N/A' ? 999999 : parseInt(ageA);
          bValue = ageB === 'N/A' ? 999999 : parseInt(ageB);
          break;
        case 'consultationDate':
          aValue = a.consultationDate ? new Date(a.consultationDate).getTime() : 0;
          bValue = b.consultationDate ? new Date(b.consultationDate).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [searchResults, sortColumn, sortDirection, calculateAge]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortTooltip = (column) => {
    if (sortColumn !== column) return 'Sort A to Z';
    return sortDirection === 'asc' ? 'Sort Z to A' : 'Sort A to Z';
  };

  const renderSortIndicator = (column) => {
    if (sortColumn !== column) {
      return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.3, marginLeft: '4px' }}>
          <path d="M7 10L12 5L17 10M7 14L12 19L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '4px' }}>
        <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ) : (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '4px' }}>
        <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    // Preserve theme before clearing user data
    const currentTheme = localStorage.getItem('theme');
    
    localStorage.removeItem('user');
    
    // Restore theme if it was removed
    if (currentTheme) {
      localStorage.setItem('theme', currentTheme);
    }
    
    setShowLogoutModal(false);
    navigate('/login-superuser', { replace: true });
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen) {
        const dropdown = document.querySelector(`.${styles.profileDropdown}`);
        const profileIcon = document.querySelector(`.${styles.profileIcon}`);
        
        if (dropdown && !dropdown.contains(event.target) && !profileIcon.contains(event.target)) {
          setIsDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Build search parameters
      const searchParams = new URLSearchParams();
      
      if (searchData.lastName.trim()) {
        searchParams.append('last_name', searchData.lastName.trim());
      }
      if (searchData.firstName.trim()) {
        searchParams.append('first_name', searchData.firstName.trim());
      }
      if (searchData.middleName.trim()) {
        searchParams.append('middle_name', searchData.middleName.trim());
      }
      if (searchData.akaNickname.trim()) {
        searchParams.append('alias_nickname', searchData.akaNickname.trim());
      }
      if (dateRangeMode === 'single' && searchData.consultationDate) {
        searchParams.append('consultation_date', searchData.consultationDate);
      } else if (dateRangeMode === 'range') {
        if (searchData.startDate) {
          searchParams.append('start_date', searchData.startDate);
        }
        if (searchData.endDate) {
          searchParams.append('end_date', searchData.endDate);
        }
      }

      // Make API call to search consultations
      const response = await authFetch(`${API_BASE_URL}/api/search-consultations?${searchParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Transform the data to match our component structure
        const transformedResults = data.consultations.map(consultation => {
          
          return {
            id: consultation.id,
            patientName: `${consultation.hr_info?.first_name || ''} ${consultation.hr_info?.middle_name || ''} ${consultation.hr_info?.last_name || ''}`.trim(),
            lastName: consultation.hr_info?.last_name || 'N/A',
            firstName: consultation.hr_info?.first_name || 'N/A',
            middleName: consultation.hr_info?.middle_name || 'N/A',
            akaNickname: consultation.hr_info?.alias_nickname || 'N/A',
            date_of_birth: consultation.hr_info?.date_of_birth,
            cell: consultation.cell,
            consultationDate: consultation.date,
            consultationType: consultation.cell_dorm || 'General',
            doctor: consultation.nod_md || 'N/A',
            status: consultation.is_archive ? 'Archived' : 'Active',
            chief_complaint: consultation.chief_complaint || 'N/A',
            diagnosis: consultation.diagnosis || 'N/A',
            diagnostics: consultation.diagnostics || 'N/A',
            referral: consultation.referral || 'N/A',
            account: consultation.account,
            nod_md: consultation.nod_md
          };
        });
        setSearchResults(transformedResults);
      } else {
        console.error('Search failed:', data.message);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      
      // Show user-friendly error message
      alert('Failed to search consultations. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = async (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);
    setIsModalLoading(true);
    
    try {
      // Fetch medications using the correct Laravel API endpoint
      const medicationsResponse = await authFetch(`${API_BASE_URL}/api/medications/consultation/${record.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });
      let medications = [];
      if (medicationsResponse.ok) {
        const medicationsData = await medicationsResponse.json();
        
        // Handle different response structures
        if (Array.isArray(medicationsData)) {
          medications = medicationsData;
        } else if (medicationsData.success && medicationsData.data) {
          medications = medicationsData.data;
        } else if (medicationsData.success && medicationsData.medications) {
          medications = medicationsData.medications;
        } else if (medicationsData.data) {
          medications = medicationsData.data;
        } else {
          medications = medicationsData;
        }
      } else {
        // Try to get error details
        try {
          await medicationsResponse.text();
        } catch (e) {
        }
        
        // For now, let's try a direct database approach - add some sample data for testing
        if (record.medications) {
          medications = record.medications;
        }
      }
      
      // Create complete record with medications
      const completeRecord = {
        ...record,
        medications: medications,
        created_at: record.created_at,
        updated_at: record.updated_at,
        cell_dorm: record.consultationType !== 'General' ? record.consultationType : record.cell_dorm,
        nod_md: record.doctor !== 'N/A' ? record.doctor : record.nod_md
      };
      setSelectedRecord(completeRecord);
      
    } catch (error) {
      console.error('Error loading consultation details:', error);
      // Use the original record if API call fails
      setSelectedRecord(record);
    } finally {
      setIsModalLoading(false);
    }
  };

  const closeModal = () => {
    setShowViewModal(false);
    setSelectedRecord(null);
  };

  // Removed update handler as Update action is no longer available

  const clearForm = () => {
    setSearchData({
      lastName: '',
      firstName: '',
      middleName: '',
      akaNickname: '',
      consultationDate: '',
      startDate: '',
      endDate: ''
    });
    setSelectedMonth('');
    setSelectedDay('');
    setSelectedYear('');
    setSearchResults([]);
    setShowDatePicker(false);
    setModalMonth('');
    setModalDay('');
    setModalYear('');
    setModalTextDate('');
    setStartModalMonth('');
    setStartModalDay('');
    setStartModalYear('');
    setStartModalTextDate('');
    setEndModalMonth('');
    setEndModalDay('');
    setEndModalYear('');
    setEndModalTextDate('');
    setDateRangeMode('single');
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calendar helper functions
  const getDaysInCalendarMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const handlePrevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  const handleDateSelect = (day) => {
    const date = new Date(calendarYear, calendarMonth, day);
    setSelectedDate(date);
    setSelectedMonth((calendarMonth + 1).toString().padStart(2, '0'));
    setSelectedDay(day.toString().padStart(2, '0'));
    setSelectedYear(calendarYear.toString());
  };

  const handleTodayClick = () => {
    const today = new Date();
    setCalendarMonth(today.getMonth());
    setCalendarYear(today.getFullYear());
    setSelectedDate(today);
    setSelectedMonth((today.getMonth() + 1).toString().padStart(2, '0'));
    setSelectedDay(today.getDate().toString().padStart(2, '0'));
    setSelectedYear(today.getFullYear().toString());
  };

  const handleApplyDate = () => {
    if (selectedDate) {
      const formattedDate = `${selectedYear}-${selectedMonth}-${selectedDay}`;
      setSearchData(prev => ({
        ...prev,
        consultationDate: formattedDate
      }));
      setShowDatePicker(false);
    }
  };

  const handleCancelDate = () => {
    setShowDatePicker(false);
    setSelectedDate(null);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const toggleDarkMode = (newMode) => {
    setPendingTheme(newMode);
    setShowThemeModal(true);
  };

  const confirmThemeChange = async () => {
    // Close modal immediately
    setShowThemeModal(false);
    
    // Update local state and storage
    setIsDarkMode(pendingTheme);
    localStorage.setItem('theme', pendingTheme ? 'dark' : 'light');
    
    try {
      // Update database in background
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          if (userData && userData.id) {
            const response = await authFetch(`${API_BASE_URL}/api/update-theme`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                user_id: userData.id,
                is_darkmode: pendingTheme ? 1 : 0
              })
            });
            
            if (!response.ok) {
              console.error('Failed to update theme in database');
            }
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    } finally {
      setPendingTheme(null);
    }
  };

  const cancelThemeChange = () => {
    setShowThemeModal(false);
    setPendingTheme(null);
  };

  return (
    <div className={`${styles.systemApp} ${isDarkMode ? '' : styles.lightMode}`}>
      {/* System Header */}
      <header className={styles.systemHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.systemLogo}>
            <img 
              src="/BJMPRO-CAR.png" 
              alt="BJMP Logo" 
              className={styles.systemLogoImg}
            />
            <div className={styles.systemTitle}>
              <h1>MediCast: BJMP Health Monitoring System</h1>
              <p>Bureau of Jail Management and Penology</p>
            </div>
          </div>
        </div>
        
        <div className={styles.headerRight}>
          <div className={styles.systemInfo}>
            <div className={styles.systemTime}>
              {formatTime(currentTime)}
            </div>
            <div className={styles.systemUser}>
              {formatDate(currentTime)}
            </div>
          </div>
          
          <div className={styles.profileSection}>
            <div 
              className={styles.profileIcon}
              onClick={toggleDropdown}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="currentColor"/>
              </svg>
            </div>
            
            {isDropdownOpen && (
              <div className={styles.profileDropdown}>
                <div className={styles.dropdownHeader}>
                  <div className={styles.userInfo}>
                    <div className={styles.userRole}>
                      {userData?.user_type === 'unit_nurse' ? 'Unit Nurse' : 
                       userData?.user_type === 'chief_nurse' ? 'Chief Nurse' : 
                       userData?.user_type === 'warden' ? 'Warden' : 
                       userData?.user_type === 'admin' ? 'System Admin' : 
                       userData?.user_type === 'jail_officer' ? 'Jail Officer' : 'User'}
                    </div>
                    <div className={styles.userName}>
                      {userData ? 
                        `${userData.last_name ? userData.last_name.charAt(0).toUpperCase() + userData.last_name.slice(1).toLowerCase() : ''}, ${userData.first_name ? userData.first_name.charAt(0).toUpperCase() + userData.first_name.slice(1).toLowerCase() : ''} ${userData.middle_name ? userData.middle_name.charAt(0).toUpperCase() + userData.middle_name.slice(1).toLowerCase() : ''}`.trim() 
                        : 'User Name'}
                    </div>
                  </div>
                </div>
                
                <div className={styles.dropdownDivider}></div>
                
                <button className={styles.dropdownItem} onClick={() => toggleDarkMode(!isDarkMode)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {isDarkMode ? (
                      <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    )}
                  </svg>
                  {isDarkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
                <button className={styles.dropdownItem} onClick={() => setShowChangePasswordModal(true)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Change Password
                </button>
                
                <button 
                  className={styles.dropdownItem}
                  onClick={handleLogout}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Date Modal for Single Date and Date Range */}
      {isDateModalOpen && (
        <div className={styles.modalOverlay} onClick={() => {
          setIsDateModalOpen(false);
          setModalError('');
        }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>📅 {dateRangeMode === 'range' ? (isStartDateModal ? 'Select Start Date' : 'Select End Date') : 'Select Consultation Date'}</h3>
              <button 
                className={styles.modalCloseBtn}
                onClick={() => {
                  setIsDateModalOpen(false);
                  setModalError('');
                }}
              >
                &times;
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.modalRow}>
                <div className={styles.modalField}>
                  <label>Month</label>
                  <select 
                    value={dateRangeMode === 'range' ? (isStartDateModal ? startModalMonth : endModalMonth) : modalMonth}
                    onChange={(e) => {
                      const value = e.target.value;
                      setModalError('');
                      
                      if (dateRangeMode === 'range') {
                        if (isStartDateModal) {
                          setStartModalMonth(value);
                          if (value && startModalDay && startModalYear) {
                            const m = value.padStart(2, '0');
                            const d = startModalDay.padStart(2, '0');
                            setStartModalTextDate(`${m}/${d}/${startModalYear}`);
                          }
                          if (startModalDay && startModalYear) {
                            const maxDays = getDaysInMonth(value, startModalYear);
                            if (parseInt(startModalDay) > maxDays) {
                              setStartModalDay(String(maxDays));
                              const m = value.padStart(2, '0');
                              setStartModalTextDate(`${m}/${String(maxDays).padStart(2, '0')}/${startModalYear}`);
                            }
                          }
                        } else {
                          setEndModalMonth(value);
                          if (value && endModalDay && endModalYear) {
                            const m = value.padStart(2, '0');
                            const d = endModalDay.padStart(2, '0');
                            setEndModalTextDate(`${m}/${d}/${endModalYear}`);
                          }
                          if (endModalDay && endModalYear) {
                            const maxDays = getDaysInMonth(value, endModalYear);
                            if (parseInt(endModalDay) > maxDays) {
                              setEndModalDay(String(maxDays));
                              const m = value.padStart(2, '0');
                              setEndModalTextDate(`${m}/${String(maxDays).padStart(2, '0')}/${endModalYear}`);
                            }
                          }
                        }
                      } else {
                        setModalMonth(value);
                        if (value && modalDay && modalYear) {
                          const m = value.padStart(2, '0');
                          const d = modalDay.padStart(2, '0');
                          setModalTextDate(`${m}/${d}/${modalYear}`);
                        }
                        if (modalDay && modalYear) {
                          const maxDays = getDaysInMonth(value, modalYear);
                          if (parseInt(modalDay) > maxDays) {
                            setModalDay(String(maxDays));
                            const m = value.padStart(2, '0');
                            setModalTextDate(`${m}/${String(maxDays).padStart(2, '0')}/${modalYear}`);
                          }
                        }
                      }
                    }}
                  >
                    <option value="">Month</option>
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>

                <div className={styles.modalField}>
                  <label>Day</label>
                  <select 
                    value={dateRangeMode === 'range' ? (isStartDateModal ? startModalDay : endModalDay) : modalDay}
                    onChange={(e) => {
                      const value = e.target.value;
                      setModalError('');
                      
                      if (dateRangeMode === 'range') {
                        if (isStartDateModal) {
                          setStartModalDay(value);
                          if (startModalMonth && value && startModalYear) {
                            const m = startModalMonth.padStart(2, '0');
                            const d = value.padStart(2, '0');
                            setStartModalTextDate(`${m}/${d}/${startModalYear}`);
                          }
                        } else {
                          setEndModalDay(value);
                          if (endModalMonth && value && endModalYear) {
                            const m = endModalMonth.padStart(2, '0');
                            const d = value.padStart(2, '0');
                            setEndModalTextDate(`${m}/${d}/${endModalYear}`);
                          }
                        }
                      } else {
                        setModalDay(value);
                        if (modalMonth && value && modalYear) {
                          const m = modalMonth.padStart(2, '0');
                          const d = value.padStart(2, '0');
                          setModalTextDate(`${m}/${d}/${modalYear}`);
                        }
                      }
                    }}
                  >
                    <option value="">Day</option>
                    {Array.from({ length: getDaysInMonth(
                      dateRangeMode === 'range' ? (isStartDateModal ? startModalMonth : endModalMonth) : modalMonth,
                      dateRangeMode === 'range' ? (isStartDateModal ? startModalYear : endModalYear) : modalYear
                    ) }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.modalField}>
                  <label>Year</label>
                  <select 
                    value={dateRangeMode === 'range' ? (isStartDateModal ? startModalYear : endModalYear) : modalYear}
                    onChange={(e) => {
                      const value = e.target.value;
                      setModalError('');
                      
                      if (dateRangeMode === 'range') {
                        if (isStartDateModal) {
                          setStartModalYear(value);
                          if (startModalMonth && startModalDay && value) {
                            const m = startModalMonth.padStart(2, '0');
                            const d = startModalDay.padStart(2, '0');
                            setStartModalTextDate(`${m}/${d}/${value}`);
                          }
                          if (startModalMonth && startModalDay) {
                            const maxDays = getDaysInMonth(startModalMonth, value);
                            if (parseInt(startModalDay) > maxDays) {
                              setStartModalDay(String(maxDays));
                              const m = startModalMonth.padStart(2, '0');
                              setStartModalTextDate(`${m}/${String(maxDays).padStart(2, '0')}/${value}`);
                            }
                          }
                        } else {
                          setEndModalYear(value);
                          if (endModalMonth && endModalDay && value) {
                            const m = endModalMonth.padStart(2, '0');
                            const d = endModalDay.padStart(2, '0');
                            setEndModalTextDate(`${m}/${d}/${value}`);
                          }
                          if (endModalMonth && endModalDay) {
                            const maxDays = getDaysInMonth(endModalMonth, value);
                            if (parseInt(endModalDay) > maxDays) {
                              setEndModalDay(String(maxDays));
                              const m = endModalMonth.padStart(2, '0');
                              setEndModalTextDate(`${m}/${String(maxDays).padStart(2, '0')}/${value}`);
                            }
                          }
                        }
                      } else {
                        setModalYear(value);
                        if (modalMonth && modalDay && value) {
                          const m = modalMonth.padStart(2, '0');
                          const d = modalDay.padStart(2, '0');
                          setModalTextDate(`${m}/${d}/${value}`);
                        }
                        if (modalMonth && modalDay) {
                          const maxDays = getDaysInMonth(modalMonth, value);
                          if (parseInt(modalDay) > maxDays) {
                            setModalDay(String(maxDays));
                            const m = modalMonth.padStart(2, '0');
                            setModalTextDate(`${m}/${String(maxDays).padStart(2, '0')}/${value}`);
                          }
                        }
                      }
                    }}
                  >
                    <option value="">Year</option>
                    {Array.from({ length: new Date().getFullYear() - 1900 + 1 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.modalRow}>
                <div className={styles.modalField} style={{ flex: 1 }}>
                  <label>Type Date (mm/dd/yyyy)</label>
                  <input
                    type="text"
                    placeholder="mm/dd/yyyy"
                    value={dateRangeMode === 'range' ? (isStartDateModal ? startModalTextDate : endModalTextDate) : modalTextDate}
                    onChange={(e) => {
                      let val = e.target.value;
                      const prevVal = dateRangeMode === 'range' ? (isStartDateModal ? startModalTextDate : endModalTextDate) : modalTextDate;
                      
                      if (val.length < prevVal.length) {
                        if (dateRangeMode === 'range') {
                          if (isStartDateModal) setStartModalTextDate(val);
                          else setEndModalTextDate(val);
                        } else {
                          setModalTextDate(val);
                        }
                        setModalError('');
                        return;
                      }
                      val = val.replace(/\D/g, '');
                      if (val.length >= 2) {
                        val = val.slice(0, 2) + '/' + val.slice(2);
                      }
                      if (val.length >= 5) {
                        val = val.slice(0, 5) + '/' + val.slice(5);
                      }
                      if (val.length > 10) {
                        val = val.slice(0, 10);
                      }
                      
                      if (dateRangeMode === 'range') {
                        if (isStartDateModal) setStartModalTextDate(val);
                        else setEndModalTextDate(val);
                      } else {
                        setModalTextDate(val);
                      }
                      setModalError('');
                      
                      const parts = val.split('/');
                      if (parts[0] && parts[0].length === 2) {
                        const m = parseInt(parts[0]);
                        if (m >= 1 && m <= 12) {
                          if (dateRangeMode === 'range') {
                            if (isStartDateModal) setStartModalMonth(String(m));
                            else setEndModalMonth(String(m));
                          } else {
                            setModalMonth(String(m));
                          }
                        }
                      }
                      if (parts[1] && parts[1].length === 2) {
                        const d = parseInt(parts[1]);
                        const maxDays = getDaysInMonth(parts[0], parts[2]);
                        if (d >= 1 && d <= maxDays) {
                          if (dateRangeMode === 'range') {
                            if (isStartDateModal) setStartModalDay(String(d));
                            else setEndModalDay(String(d));
                          } else {
                            setModalDay(String(d));
                          }
                        }
                      }
                      if (parts[2] && parts[2].length === 4) {
                        const y = parseInt(parts[2]);
                        if (y <= new Date().getFullYear()) {
                          if (dateRangeMode === 'range') {
                            if (isStartDateModal) setStartModalYear(String(y));
                            else setEndModalYear(String(y));
                          } else {
                            setModalYear(String(y));
                          }
                        }
                      }
                    }}
                    maxLength={10}
                  />
                </div>
              </div>

              {modalError && (
                <div className={styles.modalError}>
                  ⚠️ {modalError}
                </div>
              )}

              <div className={styles.modalHelper}>
                ℹ️ Select from dropdowns or type the date directly
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button 
                className={styles.modalBtnGhost}
                onClick={() => {
                  setIsDateModalOpen(false);
                  if (dateRangeMode === 'single') {
                    setModalMonth('');
                    setModalDay('');
                    setModalYear('');
                    setModalTextDate('');
                  } else {
                    if (isStartDateModal) {
                      setStartModalMonth('');
                      setStartModalDay('');
                      setStartModalYear('');
                      setStartModalTextDate('');
                    } else {
                      setEndModalMonth('');
                      setEndModalDay('');
                      setEndModalYear('');
                      setEndModalTextDate('');
                    }
                  }
                  setModalError('');
                }}
              >
                Cancel
              </button>
              <button 
                className={styles.modalBtn}
                onClick={() => {
                  if (dateRangeMode === 'single') {
                    // Single date validation
                    if (!modalMonth || !modalDay || !modalYear) {
                      setModalError('Please select month, day, and year');
                      return;
                    }
                    const m = parseInt(modalMonth);
                    const d = parseInt(modalDay);
                    const y = parseInt(modalYear);
                    if (m < 1 || m > 12) {
                      setModalError('Invalid month');
                      return;
                    }
                    const maxDays = getDaysInMonth(modalMonth, modalYear);
                    if (d < 1 || d > maxDays) {
                      setModalError(`Invalid day for selected month (max ${maxDays})`);
                      return;
                    }
                    const currentYear = new Date().getFullYear();
                    if (y > currentYear) {
                      setModalError(`Year cannot be greater than ${currentYear}`);
                      return;
                    }
                    const formattedMonth = modalMonth.padStart(2, '0');
                    const formattedDay = modalDay.padStart(2, '0');
                    const isoDate = `${modalYear}-${formattedMonth}-${formattedDay}`;
                    setSearchData(prev => ({ ...prev, consultationDate: isoDate }));
                    setIsDateModalOpen(false);
                    setModalError('');
                  } else {
                    // Date range validation
                    if (isStartDateModal) {
                      if (!startModalMonth || !startModalDay || !startModalYear) {
                        setModalError('Please select month, day, and year');
                        return;
                      }
                      const m = parseInt(startModalMonth);
                      const d = parseInt(startModalDay);
                      const y = parseInt(startModalYear);
                      if (m < 1 || m > 12) {
                        setModalError('Invalid month');
                        return;
                      }
                      const maxDays = getDaysInMonth(startModalMonth, startModalYear);
                      if (d < 1 || d > maxDays) {
                        setModalError(`Invalid day for selected month (max ${maxDays})`);
                        return;
                      }
                      const currentYear = new Date().getFullYear();
                      if (y > currentYear) {
                        setModalError(`Year cannot be greater than ${currentYear}`);
                        return;
                      }
                      const formattedMonth = startModalMonth.padStart(2, '0');
                      const formattedDay = startModalDay.padStart(2, '0');
                      const isoDate = `${startModalYear}-${formattedMonth}-${formattedDay}`;
                      
                      // Validate start date is before end date if end date exists
                      if (searchData.endDate) {
                        const startDate = new Date(isoDate);
                        const endDate = new Date(searchData.endDate);
                        if (startDate > endDate) {
                          setModalError('Start date cannot be after end date');
                          return;
                        }
                      }
                      
                      setSearchData(prev => ({ ...prev, startDate: isoDate }));
                      setIsDateModalOpen(false);
                      setModalError('');
                    } else {
                      if (!endModalMonth || !endModalDay || !endModalYear) {
                        setModalError('Please select month, day, and year');
                        return;
                      }
                      const m = parseInt(endModalMonth);
                      const d = parseInt(endModalDay);
                      const y = parseInt(endModalYear);
                      if (m < 1 || m > 12) {
                        setModalError('Invalid month');
                        return;
                      }
                      const maxDays = getDaysInMonth(endModalMonth, endModalYear);
                      if (d < 1 || d > maxDays) {
                        setModalError(`Invalid day for selected month (max ${maxDays})`);
                        return;
                      }
                      const currentYear = new Date().getFullYear();
                      if (y > currentYear) {
                        setModalError(`Year cannot be greater than ${currentYear}`);
                        return;
                      }
                      const formattedMonth = endModalMonth.padStart(2, '0');
                      const formattedDay = endModalDay.padStart(2, '0');
                      const isoDate = `${endModalYear}-${formattedMonth}-${formattedDay}`;
                      
                      // Validate end date is after start date if start date exists
                      if (searchData.startDate) {
                        const startDate = new Date(searchData.startDate);
                        const endDate = new Date(isoDate);
                        if (endDate < startDate) {
                          setModalError('End date cannot be before start date');
                          return;
                        }
                      }
                      
                      setSearchData(prev => ({ ...prev, endDate: isoDate }));
                      setIsDateModalOpen(false);
                      setModalError('');
                    }
                  }
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={styles.jbrSearchPage}>
        <div className={styles.jbrHeader}>
          <button 
            className={styles.jbrBackBtn}
            onClick={() => navigate('/dashboard')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Dashboard
          </button>
          <h1 className={styles.jbrTitle}>PDL Consultation Search</h1>
        </div>

        {/* Search Form */}
        <div className={styles.searchCard}>
          <div className={styles.searchHeader}>
            <div className={styles.searchHeaderIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2V22M17 7H7M17 17H7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                <path d="M20 9V15C20 15.5304 19.7893 16.0391 19.4142 16.4142C19.0391 16.7893 18.5304 17 18 17H6C5.46957 17 4.96086 16.7893 4.58579 16.4142C4.21071 16.0391 4 15.5304 4 15V9C4 8.46957 4.21071 7.96086 4.58579 7.58579C4.96086 7.21071 5.46957 7 6 7H18C18.5304 7 19.0391 7.21071 19.4142 7.58579C19.7893 7.96086 20 8.46957 20 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className={styles.searchHeaderText}>
              <h2>Search Consultation Records</h2>
              <p>Find consultation records by patient information</p>
            </div>
          </div>
          
          <form className={styles.searchForm} onSubmit={handleSearch}>
            <div className={styles.searchFormGrid}>
              <div className={styles.inputGroup}>
                <div className={styles.inputIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                    <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={searchData.lastName}
                    onChange={handleInputChange}
                    onKeyDown={lettersOnly}
                    className={styles.searchInput}
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <div className={styles.inputIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                    <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={searchData.firstName}
                    onChange={handleInputChange}
                    onKeyDown={lettersOnly}
                    className={styles.searchInput}
                    placeholder="Enter first name"
                  />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <div className={styles.inputIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                    <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>Middle Name</label>
                  <input
                    type="text"
                    name="middleName"
                    value={searchData.middleName}
                    onChange={handleInputChange}
                    onKeyDown={lettersOnly}
                    className={styles.searchInput}
                    placeholder="Enter middle name"
                  />
                </div>
              </div>

              <div className={styles.inputGroup}>
                <div className={styles.inputIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                    <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>AKA/Nickname</label>
                  <input
                    type="text"
                    name="akaNickname"
                    value={searchData.akaNickname}
                    onChange={handleInputChange}
                    onKeyDown={lettersOnly}
                    className={styles.searchInput}
                    placeholder="Enter nickname or alias"
                  />
                </div>
              </div>
            </div>

            {/* Second Row - Date Filter Fields */}
            <div className={styles.searchFormGrid} style={{ marginTop: '1.5rem' }}>
              {/* Date Range Mode Selector */}
              <div className={styles.inputGroup}>
                <div className={styles.inputIcon}>
                  <FaCalendarAlt />
                </div>
                <div className={styles.inputWrapper}>
                  <label className={styles.inputLabel}>Date Filter Mode</label>
                  <select
                    value={dateRangeMode}
                    onChange={(e) => {
                      setDateRangeMode(e.target.value);
                      // Clear all date fields when switching modes
                      setSearchData(prev => ({
                        ...prev,
                        consultationDate: '',
                        startDate: '',
                        endDate: ''
                      }));
                      setModalMonth('');
                      setModalDay('');
                      setModalYear('');
                      setModalTextDate('');
                      setStartModalMonth('');
                      setStartModalDay('');
                      setStartModalYear('');
                      setStartModalTextDate('');
                      setEndModalMonth('');
                      setEndModalDay('');
                      setEndModalYear('');
                      setEndModalTextDate('');
                    }}
                    className={styles.searchInput}
                  >
                    <option value="single">Single Date</option>
                    <option value="range">Date Range</option>
                  </select>
                </div>
              </div>

              {/* Single Date Input */}
              {dateRangeMode === 'single' && (
                <div className={styles.inputGroup}>
                  <div className={styles.inputIcon}>
                    <FaCalendarAlt />
                  </div>
                  <div className={styles.inputWrapper}>
                    <label className={styles.inputLabel}>Consultation Date</label>
                    <input
                      type="text"
                      name="consultationDate"
                      value={searchData.consultationDate ? formatDateForDisplay(searchData.consultationDate) : ''}
                      placeholder="mm/dd/yyyy"
                      readOnly
                      onClick={() => {
                        setIsDateModalOpen(true);
                        if (searchData.consultationDate) {
                          const date = new Date(searchData.consultationDate);
                          const m = String(date.getMonth() + 1);
                          const d = String(date.getDate());
                          const y = String(date.getFullYear());
                          setModalMonth(m);
                          setModalDay(d);
                          setModalYear(y);
                          setModalTextDate(`${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`);
                        } else {
                          setModalMonth('');
                          setModalDay('');
                          setModalYear('');
                          setModalTextDate('');
                        }
                        setModalError('');
                      }}
                      className={`${styles.searchInput} ${styles.datePickerInput}`}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
              )}

              {/* Date Range Inputs */}
              {dateRangeMode === 'range' && (
                <>
                  <div className={styles.inputGroup}>
                    <div className={styles.inputIcon}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                        <line x1="9" y1="2" x2="9" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="15" y1="2" x2="15" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className={styles.inputWrapper}>
                      <label className={styles.inputLabel}>Start Date</label>
                      <input
                        type="text"
                        name="startDate"
                        value={searchData.startDate ? formatDateForDisplay(searchData.startDate) : ''}
                        placeholder="mm/dd/yyyy"
                        readOnly
                        onClick={() => {
                          setIsStartDateModal(true);
                          setIsDateModalOpen(true);
                          if (searchData.startDate) {
                            const date = new Date(searchData.startDate);
                            const m = String(date.getMonth() + 1);
                            const d = String(date.getDate());
                            const y = String(date.getFullYear());
                            setStartModalMonth(m);
                            setStartModalDay(d);
                            setStartModalYear(y);
                            setStartModalTextDate(`${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`);
                          } else {
                            setStartModalMonth('');
                            setStartModalDay('');
                            setStartModalYear('');
                            setStartModalTextDate('');
                          }
                          setModalError('');
                        }}
                        className={`${styles.searchInput} ${styles.datePickerInput}`}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>

                  <div className={styles.inputGroup}>
                    <div className={styles.inputIcon}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                        <line x1="9" y1="2" x2="9" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="15" y1="2" x2="15" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className={styles.inputWrapper}>
                      <label className={styles.inputLabel}>End Date</label>
                      <input
                        type="text"
                        name="endDate"
                        value={searchData.endDate ? formatDateForDisplay(searchData.endDate) : ''}
                        placeholder="mm/dd/yyyy"
                        readOnly
                        onClick={() => {
                          setIsStartDateModal(false);
                          setIsDateModalOpen(true);
                          if (searchData.endDate) {
                            const date = new Date(searchData.endDate);
                            const m = String(date.getMonth() + 1);
                            const d = String(date.getDate());
                            const y = String(date.getFullYear());
                            setEndModalMonth(m);
                            setEndModalDay(d);
                            setEndModalYear(y);
                            setEndModalTextDate(`${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y}`);
                          } else {
                            setEndModalMonth('');
                            setEndModalDay('');
                            setEndModalYear('');
                            setEndModalTextDate('');
                          }
                          setModalError('');
                        }}
                        className={`${styles.searchInput} ${styles.datePickerInput}`}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className={styles.searchActions}>
              <button
                type="submit"
                className={styles.searchButton}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className={styles.searchSpinner}></div>
                    Searching...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.searchIcon}>
                      <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                      <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Search Records
                  </>
                )}
              </button>
              <button
                type="button"
                className={styles.clearButton}
                onClick={clearForm}
              >
                Clear
              </button>
            </div>
          </form>
        </div>

        {/* Results Section */}
        {searchResults.length > 0 && (
          <div className={styles.resultsSection}>
            <div className={styles.resultsHeader}>
              <div className={styles.resultsHeaderIcon}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2V22M17 7H7M17 17H7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  <path d="M20 9V15C20 15.5304 19.7893 16.0391 19.4142 16.4142C19.0391 16.7893 18.5304 17 18 17H6C5.46957 17 4.96086 16.7893 4.58579 16.4142C4.21071 16.0391 4 15.5304 4 15V9C4 8.46957 4.21071 7.96086 4.58579 7.58579C4.96086 7.21071 5.46957 7 6 7H18C18.5304 7 19.0391 7.21071 19.4142 7.58579C19.7893 7.96086 20 8.46957 20 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className={styles.resultsHeaderText}>
                <h3>Consultation Records Found</h3>
                <p>{searchResults.length} record(s) found</p>
              </div>
            </div>

            <div className={styles.resultsContainer}>
              <div className={styles.resultsTableContainer}>
                <table className={styles.resultsTable}>
                  <thead>
                    <tr>
                      <th className={styles.tableHeader}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          Current Cell
                        </div>
                      </th>
                      <th className={styles.tableHeader} onClick={() => handleSort('lastName')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('lastName')}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          Last Name
                          {renderSortIndicator('lastName')}
                        </div>
                      </th>
                      <th className={styles.tableHeader} onClick={() => handleSort('firstName')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('firstName')}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          First Name
                          {renderSortIndicator('firstName')}
                        </div>
                      </th>
                      <th className={styles.tableHeader} onClick={() => handleSort('middleName')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('middleName')}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          Middle Name
                          {renderSortIndicator('middleName')}
                        </div>
                      </th>
                      <th className={styles.tableHeader} onClick={() => handleSort('aka')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('aka')}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          AKA
                          {renderSortIndicator('aka')}
                        </div>
                      </th>
                      <th className={styles.tableHeader} onClick={() => handleSort('age')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('age')}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          Age
                          {renderSortIndicator('age')}
                        </div>
                      </th>
                      <th className={styles.tableHeader} onClick={() => handleSort('consultationDate')} style={{ cursor: 'pointer', userSelect: 'none' }} title={getSortTooltip('consultationDate')}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          Consultation Date
                          {renderSortIndicator('consultationDate')}
                        </div>
                      </th>
                      <th className={styles.tableHeader}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedResults.map((record, index) => (
                      <tr key={record.id} className={styles.tableRow}>
                        <td className={styles.tableCell}>
                          <span className={`${styles.cellBadge} ${record.cell?.cell_no ? styles.cellBadgeAssigned : styles.cellBadgeUnassigned}`}>
                            {record.cell?.cell_no || 'Unassigned'}
                          </span>
                        </td>
                        <td className={styles.tableCell}>
                          <span className={styles.cellText}>{capitalizeName(record.lastName)}</span>
                        </td>
                        <td className={styles.tableCell}>
                          <span className={styles.cellText}>{capitalizeName(record.firstName)}</span>
                        </td>
                        <td className={styles.tableCell}>
                          <span className={styles.cellText}>{capitalizeName(record.middleName)}</span>
                        </td>
                        <td className={styles.tableCell}>
                          <span className={styles.cellText}>{capitalizeName(record.akaNickname)}</span>
                        </td>
                        <td className={styles.tableCell}>
                          <span className={styles.cellText}>{calculateAge(record.date_of_birth)}</span>
                        </td>
                        <td className={styles.tableCell}>
                          <svg width={14} height={14} style={{ marginRight: '8px', color: '#60a5fa' }} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                          </svg>
                          {new Date(record.consultationDate).toLocaleDateString()}
                        </td>
                        <td className={styles.tableCell}>
                          <div className={styles.actionButtons}>
                            <button
                              className={`${styles.actionButton} ${styles.viewButton}`}
                              onClick={() => handleView(record)}
                              title="View Details"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              View
                            </button>
                            
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && searchResults.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <svg width={48} height={48} viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2a5 5 0 1 0 5 5 5 5 0 0 0-5-5zM12 4a3 3 0 1 1-3 3 3 3 0 0 1 3-3z"/>
                <path d="M12 12c-2.67 0-8 1.34-8 4v4h16v-4c0-2.66-5.33-4-8-4z"/>
                <path d="M20 6h-2v2h-2V6h-2V4h2V2h2v2h2v2z"/>
              </svg>
            </div>
            <h4>No consultation records found</h4>
            <p>Try adjusting your search criteria or check if the patient has any consultation records.</p>
          </div>
        )}
      </div>

      {/* View Modal - Redesigned */}
      {showViewModal && selectedRecord && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.viewModalContent} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header with Patient Info */}
            <div className={styles.viewModalHeader}>
              <div className={styles.patientHeaderInfo}>
                <div className={styles.patientIconLarge}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className={styles.patientHeaderText}>
                  <h2>{selectedRecord.patientName}</h2>
                  <div className={styles.patientMetadata}>
                    <span className={styles.metadataItem}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 2V5M16 2V5M3.5 9.09H20.5M21 8.5V17C21 20 19.5 22 16 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {new Date(selectedRecord.consultationDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </span>
                    {selectedRecord.date_of_birth && (
                      <span className={styles.metadataItem}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        Age: {calculateAge(selectedRecord.date_of_birth)} years
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button className={styles.viewModalCloseBtn} onClick={closeModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            {isModalLoading ? (
              <div className={styles.viewModalLoading}>
                <div className={styles.loadingSpinner}></div>
                <p>Loading consultation details...</p>
              </div>
            ) : (
              <div className={styles.viewModalBody}>
                {/* Patient Information Card */}
                <div className={styles.infoCard}>
                  <div className={styles.cardHeader}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <h3>Patient Information</h3>
                  </div>
                  <div className={styles.cardContent}>
                    <div className={styles.infoRow}>
                      <div className={styles.infoLabel}>Full Name</div>
                      <div className={styles.infoValue}>{selectedRecord.patientName}</div>
                    </div>
                    <div className={styles.infoRow}>
                      <div className={styles.infoLabel}>AKA/Nickname</div>
                      <div className={styles.infoValue}>{selectedRecord.akaNickname || 'N/A'}</div>
                    </div>
                    <div className={styles.infoRow}>
                      <div className={styles.infoLabel}>Cell/Dorm Assignment</div>
                      <div className={styles.infoValue}>
                        <span className={`${styles.viewModalCellBadge} ${selectedRecord.cell?.cell_no ? styles.viewModalCellAssigned : styles.viewModalCellUnassigned}`}>
                          {selectedRecord.cell?.cell_no || 'Unassigned'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Medical Details Card */}
                <div className={styles.infoCard}>
                  <div className={styles.cardHeader}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 7H15M9 11H15M9 15H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <h3>Medical Assessment</h3>
                  </div>
                  <div className={styles.cardContent}>
                    <div className={styles.medicalDetail}>
                      <div className={styles.medicalLabel}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                          <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <circle cx="12" cy="16" r="1" fill="currentColor"/>
                        </svg>
                        Chief Complaint
                      </div>
                      <div className={styles.medicalValue}>{selectedRecord.chief_complaint || 'Not specified'}</div>
                    </div>
                    <div className={styles.medicalDetail}>
                      <div className={styles.medicalLabel}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Diagnosis
                      </div>
                      <div className={styles.medicalValue}>{selectedRecord.diagnosis || 'Not specified'}</div>
                    </div>
                    <div className={styles.medicalDetail}>
                      <div className={styles.medicalLabel}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Diagnostics
                      </div>
                      <div className={styles.medicalValue}>{selectedRecord.diagnostics || 'Not specified'}</div>
                    </div>
                    <div className={styles.medicalDetail}>
                      <div className={styles.medicalLabel}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17 8L21 12M21 12L17 16M21 12H9M9 4H7C6.46957 4 5.96086 4.21071 5.58579 4.58579C5.21071 4.96086 5 5.46957 5 6V18C5 18.5304 5.21071 19.0391 5.58579 19.4142C5.96086 19.7893 6.46957 20 7 20H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Referral
                      </div>
                      <div className={styles.medicalValue}>{selectedRecord.referral || 'No referral needed'}</div>
                    </div>
                  </div>
                </div>

                {/* Medications Card */}
                <div className={styles.infoCard}>
                  <div className={styles.cardHeader}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.5 16.5C3 15 3 12.5 4.5 11L11 4.5C12.5 3 15 3 16.5 4.5C18 6 18 8.5 16.5 10L10 16.5C8.5 18 6 18 4.5 16.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M13.5 7.5L16.5 10.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8.5 12.5L11.5 15.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14.5 17.5C15 17 15 16 14.5 15.5L8.5 9.5C8 9 7 9 6.5 9.5C6 10 6 11 6.5 11.5L12.5 17.5C13 18 14 18 14.5 17.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <h3>Prescribed Medications</h3>
                    {selectedRecord.medications && selectedRecord.medications.length > 0 && (
                      <span className={styles.medicationCount}>{selectedRecord.medications.length}</span>
                    )}
                  </div>
                  <div className={styles.cardContent}>
                    {selectedRecord.medications && selectedRecord.medications.length > 0 ? (
                      <div className={styles.medicationsGrid}>
                        {selectedRecord.medications.map((medication, index) => (
                          <div key={index} className={styles.medicationCard}>
                            <div className={styles.medicationHeader}>
                              <span className={styles.medicationNumber}>#{index + 1}</span>
                              <span className={styles.medicationName}>{medication.name_of_medication || 'N/A'}</span>
                            </div>
                            <div className={styles.medicationDetails}>
                              <div className={styles.medicationDetailItem}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 8V16M8 12H16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span className={styles.medicationDetailLabel}>Dosage:</span>
                                <span className={styles.medicationDetailValue}>{medication.dosage || 'N/A'}</span>
                              </div>
                              <div className={styles.medicationDetailItem}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9 11H15M9 15H15M12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                <span className={styles.medicationDetailLabel}>Quantity:</span>
                                <span className={styles.medicationDetailValue}>{medication.quantity || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.noMedications}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.5 16.5C3 15 3 12.5 4.5 11L11 4.5C12.5 3 15 3 16.5 4.5C18 6 18 8.5 16.5 10L10 16.5C8.5 18 6 18 4.5 16.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.3"/>
                          <path d="M2 22L7 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.3"/>
                        </svg>
                        <p>No medications prescribed for this consultation</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Healthcare Provider Card */}
                <div className={styles.infoCard}>
                  <div className={styles.cardHeader}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <h3>Healthcare Provider</h3>
                  </div>
                  <div className={styles.cardContent}>
                    <div className={styles.providerInfo}>
                      <div className={styles.providerAvatar}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className={styles.providerDetails}>
                        <div className={styles.providerName}>
                          {selectedRecord.account ? 
                            `Dr. ${selectedRecord.account.first_name} ${selectedRecord.account.last_name}` : 
                            selectedRecord.nod_md || 'Not assigned'
                          }
                        </div>
                        <div className={styles.providerRole}>Attending Physician (NOD/MD)</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div className={styles.datePickerOverlay} onClick={handleCancelDate}>
          <div className={styles.calendarModal} onClick={(e) => e.stopPropagation()}>
            {/* Calendar Header */}
            <div className={styles.calendarHeader}>
              <button 
                className={styles.calendarNavBtn}
                onClick={handlePrevMonth}
                aria-label="Previous month"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              <h3 className={styles.calendarTitle}>
                {monthNames[calendarMonth]} {calendarYear}
              </h3>
              
              <button 
                className={styles.calendarNavBtn}
                onClick={handleNextMonth}
                aria-label="Next month"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Date Display and Today Button */}
            <div className={styles.calendarInfo}>
              <div className={styles.selectedDateDisplay}>
                {selectedDate ? (
                  `${(selectedDate.getMonth() + 1).toString().padStart(2, '0')} / ${selectedDate.getDate().toString().padStart(2, '0')} / ${selectedDate.getFullYear()}`
                ) : (
                  'Select a date'
                )}
              </div>
              <button className={styles.todayBtn} onClick={handleTodayClick}>
                Today
              </button>
            </div>

            {/* Calendar Grid */}
            <div className={styles.calendarGrid}>
              {/* Day Headers */}
              <div className={styles.dayHeaders}>
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                  <div key={day} className={styles.dayHeader}>{day}</div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className={styles.daysGrid}>
                {(() => {
                  const daysInMonth = getDaysInCalendarMonth(calendarMonth, calendarYear);
                  const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear);
                  const prevMonthDays = calendarMonth === 0 
                    ? getDaysInCalendarMonth(11, calendarYear - 1)
                    : getDaysInCalendarMonth(calendarMonth - 1, calendarYear);
                  
                  const days = [];
                  
                  // Previous month days
                  for (let i = firstDay - 1; i >= 0; i--) {
                    days.push(
                      <button
                        key={`prev-${i}`}
                        className={`${styles.dayCell} ${styles.otherMonth}`}
                        disabled
                      >
                        {prevMonthDays - i}
                      </button>
                    );
                  }
                  
                  // Current month days
                  const today = new Date();
                  const isCurrentMonth = calendarMonth === today.getMonth() && calendarYear === today.getFullYear();
                  
                  for (let day = 1; day <= daysInMonth; day++) {
                    const isToday = isCurrentMonth && day === today.getDate();
                    const isSelected = selectedDate && 
                      selectedDate.getDate() === day && 
                      selectedDate.getMonth() === calendarMonth && 
                      selectedDate.getFullYear() === calendarYear;
                    
                    days.push(
                      <button
                        key={`current-${day}`}
                        className={`${styles.dayCell} ${isToday ? styles.today : ''} ${isSelected ? styles.selected : ''}`}
                        onClick={() => handleDateSelect(day)}
                      >
                        {day}
                      </button>
                    );
                  }
                  
                  // Next month days to fill the grid
                  const remainingCells = 42 - days.length;
                  for (let day = 1; day <= remainingCells; day++) {
                    days.push(
                      <button
                        key={`next-${day}`}
                        className={`${styles.dayCell} ${styles.otherMonth}`}
                        disabled
                      >
                        {day}
                      </button>
                    );
                  }
                  
                  return days;
                })()}
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.calendarActions}>
              <button 
                className={styles.calendarCancelBtn}
                onClick={handleCancelDate}
              >
                Cancel
              </button>
              <button 
                className={styles.calendarApplyBtn}
                onClick={handleApplyDate}
                disabled={!selectedDate}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        userData={JSON.parse(localStorage.getItem('user'))}
        onSuccess={(msg) => {
          setSuccessMessage(msg);
          setShowSuccessModal(true);
        }}
        onError={(msg) => {
          setErrorMessage(msg);
          setShowErrorModal(true);
        }}
      />

      {/* Success Modal */}
      {showSuccessModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowSuccessModal(false)}>
          <div className={styles.successModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Success</h3>
            <p>{successMessage}</p>
            <button className={styles.successBtn} onClick={() => setShowSuccessModal(false)}>
              OK
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Error Modal */}
      {showErrorModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={() => setShowErrorModal(false)}>
          <div className={styles.errorModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.errorIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Error</h3>
            <p>{errorMessage}</p>
            <button className={styles.errorBtn} onClick={() => setShowErrorModal(false)}>
              Close
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Theme Change Confirmation Modal */}
      {showThemeModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={cancelThemeChange}>
          <div className={styles.themeModal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#f59e0b' }}>
            <div className={styles.themeIcon} style={{ background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)', color: '#f59e0b' }}>
              {pendingTheme ? (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
            <h3 style={{ color: '#f59e0b' }}>Change Theme</h3>
            <p>Are you sure you want to switch to <strong>{pendingTheme ? 'Dark' : 'Light'} Mode</strong>?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className={styles.cancelBtn} onClick={cancelThemeChange}>Cancel</button>
              <button className={styles.themeBtn} onClick={confirmThemeChange} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>Confirm</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && ReactDOM.createPortal(
        <div className={styles.modalOverlay} onClick={cancelLogout}>
          <div className={styles.themeModal} onClick={(e) => e.stopPropagation()} style={{ borderColor: '#ef4444' }}>
            <div className={styles.themeIcon} style={{ background: 'rgba(239, 68, 68, 0.1)', borderColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="16,17 21,12 16,7" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="21" y1="12" x2="9" y2="12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 style={{ color: '#ef4444' }}>Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className={styles.cancelBtn} onClick={cancelLogout}>Cancel</button>
              <button className={styles.themeBtn} onClick={confirmLogout} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>Logout</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default PdlConsultationSearch;
